import { PublicClientApplication } from "@azure/msal-browser";
import axios, { AxiosError } from "axios";
import { loginRequest, msalConfig } from "./useAuth";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import store from "app/store";
import { openModalError } from "app/store/trackingApp/modalErrorSlice";
import getEnv from "env";

const msalInstance = new PublicClientApplication(msalConfig);

const REACT_APP_VIATRACKING_API_URL = getEnv("REACT_APP_VIATRACKING_API_URL");

export const getAccessTokenStorage = () =>
  sessionStorage.getItem("AccessToken");

export const getUserOID = () => {
  const sessionId: any = sessionStorage.getItem("SessionID");
  return sessionId;
};

const requestConfigInterceptor = (config) => {
  const accessToken = getAccessTokenStorage();

  const customConfig = {
    ...config,
    headers: { ...config.headers, "viatracking-session-id": getUserOID() },
  };

  if (!accessToken) {
    throw new Error("no access token");
  }

  customConfig.headers.Authorization = `Bearer ${accessToken}`;

  return customConfig;
};

export const apiInstance = axios.create({
  baseURL: `${REACT_APP_VIATRACKING_API_URL}/transportation/viatracking/v1.0`,
  timeout: 200000,
  headers: {
    "viatracking-session-id": getUserOID(),
  },
});

export const authInstance = axios.create({
  baseURL: `${REACT_APP_VIATRACKING_API_URL}/transportation/viatracking/v1.0`,
});

const useResponseErrorInterceptor = async (error) => {
  if (error.response) {
    if (error.response.status === 401) {
      await msalInstance.logoutRedirect(loginRequest);
    } else if (error.response.status === 403) {
      store.dispatch(
        openModalError({
          title: "Acesso negado",
          text: "Desculpe, você não tem permissão para acessar este recurso. Por favor, verifique suas credenciais e tente novamente ou entre em contato com o administrador para obter mais informações.",
          btnLabel: "Fechar",
        })
      );
    } else {
      return Promise.reject(error.response);
    }
  }
};

const withInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use(requestConfigInterceptor, (error) =>
    Promise.reject(error)
  );
  axiosInstance.interceptors.response.use(
    (response) => response,
    useResponseErrorInterceptor
  );

  return axiosInstance;
};

export const API = withInterceptor(apiInstance);

export const successHandler = (data) => ({
  success: true,
  data,
});

export const errorHandler = (resp) => ({
  success: false,
  data: {
    status: resp?.data?.status || resp?.status,
    response: resp?.data,
  },
});

export const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string } = { baseUrl: "" }): BaseQueryFn =>
  async ({ url, method, data, params }) => {
    try {
      const result = await apiInstance({
        url: baseUrl + url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
